import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { Panel, Chip, Th, Td } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg, fmt, ldnTime } from "@/lib/api";

const TONE = { open: "LIVE", closed: "DONE", waiting_market: "PENDING", rejected: "TRIGGERED", close_failed: "WARNING", cancelled: "SKIPPED", position_already_gone: "SKIPPED", placing: "PENDING" };

export function LiveOrderTest({ connected }) {
  const [markets, setMarkets] = useState([]);
  const [f, setF] = useState({ key: "GOLD", side: 1, volume: "0.01", hold_minutes: "15" });
  const [orders, setOrders] = useState([]);
  const load = useCallback(async () => setOrders((await api.get("/broker/test-orders")).data), []);
  useEffect(() => {
    api.get("/levels/state").then((r) => setMarkets(r.data.board.map((b) => b.market)));
    load();
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, [load]);
  const place = async (key) => {
    const m = markets.find((x) => x.key === key);
    if (!m) return;
    if (!window.confirm(`Send a real ${f.side > 0 ? "buy" : "sell"} order for ${f.volume} lots of ${m.name} (${m.symbol}) to MT5?`)) return;
    try {
      const { data } = await api.post("/broker/test-orders", { symbol: m.symbol, label: m.name, side: f.side, volume: Number(f.volume), hold_minutes: Number(f.hold_minutes), wait_for_open: true });
      toast.success(`${m.name}: ${data.status.replaceAll("_", " ")}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const close = async (id) => { try { await api.post(`/broker/test-orders/${id}/close`); load(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Panel title="Live order test (demo)" testid="live-order-test" className="lg:col-span-2">
      <p className="mb-4 text-xs text-slate-400">Sends a real market order to MT5 with a stop 1% away, then closes it automatically after the hold time. If the market is closed it waits and places the order as soon as it opens.</p>
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-xs text-slate-400">Market
          <select data-testid="test-order-market" value={f.key} onChange={(e) => setF({ ...f, key: e.target.value })} className="mt-1 block h-9 rounded-md border border-slate-700 bg-slate-950 px-2 text-sm text-slate-200">
            {markets.map((m) => <option key={m.key} value={m.key}>{m.name} ({m.symbol})</option>)}
          </select>
        </label>
        <label className="text-xs text-slate-400">Side
          <select data-testid="test-order-side" value={f.side} onChange={(e) => setF({ ...f, side: Number(e.target.value) })} className="mt-1 block h-9 rounded-md border border-slate-700 bg-slate-950 px-2 text-sm text-slate-200"><option value={1}>Buy</option><option value={-1}>Sell</option></select>
        </label>
        <label className="text-xs text-slate-400">Lots<Input data-testid="test-order-volume" value={f.volume} onChange={(e) => setF({ ...f, volume: e.target.value })} className="num mt-1 h-9 w-20 bg-slate-950" /></label>
        <label className="text-xs text-slate-400">Hold (min)<Input data-testid="test-order-hold" value={f.hold_minutes} onChange={(e) => setF({ ...f, hold_minutes: e.target.value })} className="num mt-1 h-9 w-20 bg-slate-950" /></label>
        <Button data-testid="test-order-place" className="btn-gold" disabled={!connected} onClick={() => place(f.key)}><Send className="mr-1.5 h-4 w-4" />Place test order</Button>
        <Button data-testid="test-order-gold-nikkei" variant="secondary" disabled={!connected} onClick={async () => { await place("GOLD"); await place("NIKKEI"); }}>Gold + Nikkei</Button>
      </div>
      {orders.length > 0 && (
        <div className="mt-5 overflow-x-auto">
          <table className="w-full min-w-[900px]">
            <thead><tr><Th>Created</Th><Th>Market</Th><Th>Side</Th><Th>Lots</Th><Th>Status</Th><Th>Fill</Th><Th>Stop</Th><Th>Position</Th><Th>Closes</Th><Th>Close</Th><Th>Note</Th><Th /></tr></thead>
            <tbody>{orders.map((o) => (
              <tr key={o.id} data-testid={`test-order-row-${o.id}`} className="border-b border-slate-800/60">
                <Td className="text-xs text-slate-400">{ldnTime(o.created_at)}</Td><Td>{o.label || o.symbol}</Td><Td>{o.side > 0 ? "Buy" : "Sell"}</Td><Td className="num">{o.volume}</Td>
                <Td><Chip status={TONE[o.status] || "NONE"} label={o.status.replaceAll("_", " ")} /></Td>
                <Td className="num">{fmt(o.fill_price)}</Td><Td className="num">{fmt(o.stop)}</Td><Td className="num text-xs">{o.position_id || "-"}</Td>
                <Td className="text-xs text-slate-400">{o.close_at ? ldnTime(o.close_at) : "-"}</Td><Td className="num">{fmt(o.close_price)}</Td>
                <Td className="max-w-[260px] truncate text-xs text-slate-400" title={o.error || ""}>{o.error || o.close_reason || ""}</Td>
                <Td>{["open", "close_failed", "waiting_market"].includes(o.status) && <Button size="sm" variant="ghost" className="text-rose-300" data-testid={`test-order-close-${o.id}`} onClick={() => close(o.id)}>{o.status === "waiting_market" ? "Cancel" : "Close now"}</Button>}</Td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}
